import { ButtonType } from '@ardoq/button';
import React, { useRef } from 'react';
import { SettingsBarButton } from '@ardoq/settings-bar';
import {
  ViewOptionsSectionRowContainer,
  ViewOptionsSectionRowContent,
  ViewOptionsSectionRowLabel,
  ViewOptionsSectionRightControls,
} from './atoms';
import { Tag } from '@ardoq/status-ui';
import {
  ChevronRightIcon,
  Icon,
  IconName,
  IconSize,
  InfoIcon,
} from '@ardoq/icons';
import {
  isDropdownConfig,
  isButtonConfig,
  isToggleConfig,
  isCustomComponentConfig,
  type SettingsConfig,
} from '@ardoq/view-settings';
import {
  ButtonWithDropdown,
  dispatchMouseEventOnTarget,
  simpleNotifier,
  Notifier,
} from '@ardoq/dropdown-menu';
import { colors } from '@ardoq/design-tokens';
import { ViewIds } from '@ardoq/api-types';
import { Switch } from '@ardoq/forms';
import {
  TextOverflow,
  PlainTextPopover,
  WithUncontrolledPopover,
} from '@ardoq/popovers';

const forwardClickOnViewSettingCustomComponent = (
  event: React.MouseEvent<HTMLDivElement>,
  controlsContainer?: Element | null
) => {
  if (
    controlsContainer &&
    controlsContainer !== event.target &&
    !controlsContainer.contains(event.target as Node)
  ) {
    const checkbox = controlsContainer.querySelector('input[type="checkbox"]');
    const clickTarget = checkbox || controlsContainer;

    dispatchMouseEventOnTarget(clickTarget);
  }
};

type GetOnClick = (
  event: React.MouseEvent<HTMLDivElement>,
  controlsContainer?: Element | null
) => void;
const getOnClick = (
  viewModifier: SettingsConfig,
  _viewId: ViewIds,
  dropdownNotifier: Notifier
): GetOnClick => {
  if (isDropdownConfig(viewModifier)) {
    return () => {
      /**
       * Guard is intentional: prevents the notifier from firing when the row container is clicked.
       */
      if (!viewModifier.isDisabled) {
        dropdownNotifier.notify();
      }
    };
  }

  if (isButtonConfig(viewModifier)) {
    return viewModifier.onClick;
  }

  return forwardClickOnViewSettingCustomComponent;
};

type ViewSettingsRowProperties = {
  viewModifier: SettingsConfig;
  displayValue: string | null;
  viewId: ViewIds;
};
const ViewSettingsRow = ({
  viewId,
  viewModifier,
  displayValue,
}: ViewSettingsRowProperties) => {
  const label = 'label' in viewModifier ? viewModifier.label : viewModifier.id;
  const iconName = 'iconName' in viewModifier ? viewModifier.iconName : null;
  const rightControlsRef = React.useRef<HTMLDivElement>(null);
  const notifier = useRef(simpleNotifier());
  const onClick = getOnClick(viewModifier, viewId, notifier.current);

  const popoverText =
    'popoverText' in viewModifier ? viewModifier.popoverText : null;

  const isDropDownConfig = isDropdownConfig(viewModifier);

  return (
    <ViewOptionsSectionRowContainer
      onClick={event =>
        onClick(event, rightControlsRef.current?.lastElementChild)
      }
      role="option"
      aria-label={label}
      aria-disabled={
        isDropdownConfig(viewModifier) && viewModifier.isDisabled
          ? true
          : undefined
      }
      data-testid="view-settings-row"
    >
      <ViewOptionsSectionRowContent>
        {iconName && (
          <Icon
            color={colors.icon}
            iconName={iconName}
            iconSize={IconSize.LARGE}
          />
        )}
        <ViewOptionsSectionRowLabel>{label}</ViewOptionsSectionRowLabel>
        {popoverText && (
          <WithUncontrolledPopover
            content={<PlainTextPopover>{popoverText}</PlainTextPopover>}
          >
            <InfoIcon color={colors.icon} iconSize={IconSize.MEDIUM} />
          </WithUncontrolledPopover>
        )}
        <ViewOptionsSectionRightControls ref={rightControlsRef}>
          {displayValue && (
            <Tag>
              <TextOverflow lineClamp={2}>{displayValue}</TextOverflow>
            </Tag>
          )}
          {isDropDownConfig ? (
            <ButtonWithDropdown
              iconName={IconName.CHEVRON_RIGHT}
              aria-label={label}
              options={viewModifier.options}
              isKeepOpen={viewModifier.isKeepOpen}
              dropdownSize={viewModifier.dropdownSize}
              maxDropdownSize={viewModifier.maxDropdownSize}
              maxDropdownHeight={viewModifier.maxDropdownHeight}
              requestOpen={notifier.current}
              buttonType={ButtonType.GHOST}
              isDisabled={viewModifier.isDisabled}
            />
          ) : isToggleConfig(viewModifier) ? (
            <Switch
              name={viewModifier.id}
              aria-label={label}
              isChecked={viewModifier.isActive}
              onChange={() =>
                viewModifier.onViewSettingsUpdate?.(
                  viewId,
                  { [viewModifier.id]: !viewModifier.isActive },
                  true
                )
              }
              isDisabled={viewModifier.isDisabled}
            />
          ) : isButtonConfig(viewModifier) ? (
            <SettingsBarButton {...viewModifier} />
          ) : isCustomComponentConfig(viewModifier) ? (
            React.createElement(
              viewModifier.component,
              viewModifier.componentProps
            )
          ) : (
            <ChevronRightIcon disabled={true} />
          )}
        </ViewOptionsSectionRightControls>
      </ViewOptionsSectionRowContent>
    </ViewOptionsSectionRowContainer>
  );
};

export { ViewSettingsRow };
